import React, { useCallback, useContext, useEffect } from 'react';

import FieldLabel from '@react/react-spectrum/FieldLabel';
import Heading from '@react/react-spectrum/Heading';
import Select from '@react/react-spectrum/Select';

import { ComboBox, Item } from '@adobe/react-spectrum';
import Wait from '@react/react-spectrum/Wait';

import { useAepSandboxes, useActiveAep } from 'root/hooks';
import { useConfigurations } from 'data/hooks';

import { QuickSetupContext } from './Context';

export default function General() {
  const { context, setContextValue } = useContext(QuickSetupContext);

  const environments = useAepSandboxes();
  const activeEnvironment = useActiveAep();

  const { loading, error, data } = useConfigurations({
    category: 'aep',
    imsOrgId: context.environment?.imsOrg?.id,
    sandboxName: context.environment?.sandboxName
  });
  const configurations = data?.configurations;

  const environmentSearch = context.environmentSearch || '';
  const configurationSearch = context.configurationSearch || '';

  // Changing the Environment may change which presets the user is allowed to
  // use, so clear the current preset selection whenever it changes.
  const selectEnvironment = useCallback(
    (value) => {
      setContextValue({ path: 'environment', value });
      setContextValue({ path: 'preset', value: null });
    },
    [setContextValue]
  );

  useEffect(() => {
    if (activeEnvironment?.id) {
      selectEnvironment(activeEnvironment);
    }
  }, [activeEnvironment, selectEnvironment]);

  useEffect(() => {
    if (Array.isArray(configurations) && configurations.length >= 1) {
      setContextValue({
        path: 'configuration',
        value: configurations[0]
      });
    }
  }, [configurations]);

  return (
    <div className="general">
      <Heading size={3}>General</Heading>

      <div className="fields">
        <FieldLabel
          label="Environment"
          necessity="required"
          className="environment-field">
          <ComboBox
            width="size-5000"
            placeholder="Select an option"
            selectedKey={
              context.environment?.id ? String(context.environment.id) : null
            }
            onSelectionChange={(key) => {
              const selectedEnv = environments.find(
                (e) => String(e.id) === key
              );
              selectEnvironment(selectedEnv);
              if (selectedEnv) {
                setContextValue({
                  path: 'environmentSearch',
                  value: `${selectedEnv.imsOrg.name}/${selectedEnv.sandboxName}`
                });
              }
            }}
            inputValue={environmentSearch}
            onInputChange={(value) => {
              setContextValue({ path: 'environmentSearch', value });
              // Clear selection if input doesn't match the selected environment
              if (context.environment) {
                const selectedLabel = `${context.environment.imsOrg.name}/${context.environment.sandboxName}`;
                if (value !== selectedLabel) {
                  selectEnvironment(null);
                }
              }
            }}
            items={environments
              .filter((environment) => {
                const label = `${environment.imsOrg.name}/${environment.sandboxName}`;
                return label
                  .toLowerCase()
                  .includes(environmentSearch.toLowerCase());
              })
              .map((environment) => ({
                id: String(environment.id),
                label: `${environment.imsOrg.name}/${environment.sandboxName}`
              }))}>
            {(item) => <Item>{item.label}</Item>}
          </ComboBox>
        </FieldLabel>

        <FieldLabel label="Configuration" necessity="required">
          {loading && <Wait size="S" />}

          {error && <span>{error.message}</span>}

          {Array.isArray(configurations) && (
            <Select
              options={configurations.map((configuration) => ({
                label: configuration.title,
                value: configuration.id,
                description: configuration.description
              }))}
              renderItem={renderConfigurationsItem}
              value={context.configuration?.id}
              onChange={(value) =>
                setContextValue({
                  path: 'configuration',
                  value: configurations.find((c) => c.id === value)
                })
              }
            />
          )}
        </FieldLabel>
      </div>
    </div>
  );
}
function renderConfigurationsItem(item) {
  return (
    <div className="configurations-item">
      <Heading variant="subtitle2">{item.label}</Heading>
      <p>{item.description}</p>
    </div>
  );
}
